'use client';

import { useEffect, useState } from 'react';
import { generateMockData } from '@/lib/browser-mockdata-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserMockDataGenerator() {
  const [count, setCount] = useState(10);
  const [format, setFormat] = useState<'json' | 'csv'>('json');
  const [output, setOutput] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  function generate() {
    setCopyMessage(null);
    setOutput(generateMockData(count, format));
  }

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Mock data</span>
        <h2 className="tool-live-title">Generate mock test data</h2>
        <p className="tool-live-subtitle">
          Create realistic fake records as JSON or CSV, generated locally in your browser. Nothing
          is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>How many rows</span>
              <input
                inputMode="numeric"
                onChange={(event) => setCount(Number(event.target.value))}
                type="number"
                value={count}
              />
            </label>
            <label className="tool-live-field">
              <span>Format</span>
              <select
                onChange={(event) => setFormat(event.target.value as 'json' | 'csv')}
                value={format}
              >
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
              </select>
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={generate} type="button">
              Generate
            </button>
            {output ? (
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy result
              </button>
            ) : null}
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Generated data</span>
            <textarea readOnly rows={12} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Generated locally with the Web Crypto API. Click generate for a fresh set.
          </p>
        </section>
      </div>
    </div>
  );
}
